import { Inject, Injectable } from '@angular/core';
import { AZURE_APPLICATION_PROXY_URLS } from '../../enums/azure-application-proxy-urls.enums';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { EXPANDS } from '../../enums/expands.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { EnvironmentInstanceToken, PaceEnvironment } from '../../interfaces/environment.interface';
import { UserModel } from '../../models/complex/user.model';
import { isDefined } from '../../utilities/is-defined';

@Injectable()
export class UriUtilitiesService {
    private readonly endpointsWithSalesOrgAndPlantFilter: string[] = [
        ENDPOINTS.ConfigSet,
        ENDPOINTS.CseDopSet,
        ENDPOINTS.InputFieldProperties,
        ENDPOINTS.ValueHelpers,
        ENDPOINTS.NotificationHeaders,
        ENDPOINTS.WhatsNew,
        ENDPOINTS.WorkScheduleSet,
        ENDPOINTS.MaterialStockSet,
    ];

    private readonly endpointsWithFrontId: string[] = [
        ENDPOINTS.WorkScheduleSet,
        ENDPOINTS.MaterialStockSet,
        ENDPOINTS.MeasToolSet,
    ];
    public constructor(
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(EnvironmentInstanceToken) private readonly environment: PaceEnvironment,
    ) {}

    public getUriForContent(): string {
        if (this.environment.useMsal || this.environment.auth0?.clientId) {
            return `${this.environment.serviceProtocol}://${this.environment.serviceHost}/${this.environment.servicePath}`;
        }

        return `${this.environment.servicePath}`;
    }

    public getUriForClientAuthentication(): string {
        const parameter = `${AZURE_APPLICATION_PROXY_URLS.AuthenticationParameterKey}=${AZURE_APPLICATION_PROXY_URLS.AuthenticationTargetEndPoint}`;
        const sapClient =
            isDefined(this.environment.sapClient) && this.environment.sapClient?.length > 0
                ? `?${this.environment.sapClient}`
                : '';
        const url = `${this.environment.serviceProtocol}://${this.environment.serviceHost}/${this.environment.servicePath}${ENDPOINTS.IsAlive}${sapClient}`;
        const format = '$format=json';

        const urlWithParam = this.appendParameter(url, parameter);
        return this.appendParameter(urlWithParam, format);
    }

    /**
     * Appends parameters to a URL.
     *
     * @param url
     * @param parameter
     */
    public appendParameter(url: string, parameter: string): string {
        const separator = url.indexOf('?') < 0 ? '?' : '&';
        return `${url}${separator}${parameter}`;
    }

    public appendSapClientToRequestUrl(url: string): string {
        if (isDefined(this.environment.sapClient) && this.environment.sapClient?.length > 0) {
            return this.appendParameter(url, `${this.environment.sapClient}`);
        }
        return url;
    }

    /**
     * Appends filters to defining requests urls
     *
     * @param definingRequests
     * @param selectedUserModel
     */
    public appendFiltersForDefiningRequests(definingRequests: string[], selectedUserModel: UserModel): string[] {
        return definingRequests.map((definingRequest) => this.appendUriFilters(definingRequest, selectedUserModel));
    }

    /**
     * Appends filters to a URL.
     *
     * @param url
     * @param selectedUserModel
     */
    public appendUriFilters(url: string, selectedUserModel: UserModel): string {
        const filterSalesOrgPlant = `SalesOrg eq '${selectedUserModel.getSalesOrg()}' and Plant eq '${selectedUserModel.getPlant()}'`;
        const filterFrontId = `FrontId eq '${selectedUserModel.getFrontId()}'`;
        if (this.endpointsWithFrontId.find((ep) => url.indexOf(ep) > -1)) {
            url = this.appendFilterValue(url, filterFrontId);
        }
        if (
            this.clientManager.isNgmAP0() &&
            this.endpointsWithSalesOrgAndPlantFilter.find((ep) => url.indexOf(ep) > -1)
        ) {
            url = this.appendFilterValue(url, filterSalesOrgPlant);
        }
        return url;
    }

    /**
     * Appends an OData filter value to a URL
     *
     * @param url
     * @param filterValue
     */
    public appendFilterValue(url: string, filterValue: string): string {
        const groups: { [keys: string]: string } = {};

        // split given URL in base URL and already existing parameters
        const separatorIndex = url.indexOf('?');
        let baseUrl = separatorIndex < 0 ? url : url.substr(0, separatorIndex);
        const urlParameters = separatorIndex >= 0 ? url.substr(separatorIndex + 1) : '';

        // if the URL already contains parameters, we split the string into individual parameters and store them for later
        if (urlParameters.length > 0) {
            const parameters = urlParameters.split('&');
            parameters.forEach((p) => {
                // extract key and value from each parameter
                const parameterAndValue = p.split('=');
                groups[parameterAndValue[0]] = parameterAndValue[1];
            });
        }

        // make sure filter entry exists
        groups.$filter = groups.$filter || '';

        // now add the new filter value
        groups.$filter = groups.$filter.length === 0 ? filterValue : `${groups.$filter} and ${filterValue}`;

        // reconstruct the entire URL

        const groupsKeys = Object.keys(groups);
        if (groupsKeys.length > 0) {
            let separator = '?';

            for (const p of groupsKeys) {
                baseUrl += `${separator}${p}=${groups[p]}`;
                separator = '&';
            }
        }

        return baseUrl;
    }

    /**
     * This function returns the notificationHeaderQuery based on the environment.
     */
    public getNotificationHeaderByAppId(): string {
        let notificationHeader = '';

        if (this.clientManager.isNgmP58()) {
            notificationHeader = EXPANDS.NotificationHeader;
        } else if (this.clientManager.isPaceUS()) {
            notificationHeader = EXPANDS.NotificationHeaderP40;
        } else if (this.clientManager.isNgmAA()) {
            notificationHeader = EXPANDS.NotificationHeaderAA;
        } else if (this.clientManager.isNgmSA()) {
            notificationHeader = EXPANDS.NotificationHeaderSA;
        }
        return notificationHeader;
    }

    /**
     * This function returns CSML url by Appending multiple Material Order number and also single order
     * TODO: move me to a proper place!
     *
     * @param materials
     * @param csmlUrl
     */
    public getCSMLTrackerURL(materials: Array<{ orderNumber: { value: string } }>, csmlUrl: string): string {
        if (!csmlUrl) {
            return '';
        }
        return materials.reduce(
            (acc, material, currentIndex) =>
                `${acc}purchaseOrder[]=${material.orderNumber.value}${currentIndex < materials.length - 1 ? '&' : ''}`,
            csmlUrl,
        );
    }
}
